import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import {
  Plus,
  Search,
  FileDown,
  ChevronDown,
  Trash2,
  Eye,
  Pencil,
} from "lucide-react";

interface ExperienceInitiativeItem {
  id: string;
  stt: number;
  initiativeName: string;
  field: string;
  totalCost: string;
  proposer: string;
  coordinatingUnit: string;
  implementationTime: string;
  status: string;
}

const mockData: ExperienceInitiativeItem[] = [
  {
    id: "1",
    stt: 1,
    initiativeName: "Sáng kiến 1 »",
    field: "Chiến lược thủy lợi của các vùng, miền và quốc gia",
    totalCost: "",
    proposer: "Thượng tá, VS. ThS Lê Anh Tuấn",
    coordinatingUnit: "Ban Tổng Giám đốc",
    implementationTime: "2 Tháng",
    status: "Mới",
  },
  {
    id: "2",
    stt: 2,
    initiativeName: "Đề xuất 1 »",
    field: "",
    totalCost: "50.000.000",
    proposer: "VS. ThS Lê Anh Tuấn",
    coordinatingUnit: "Khoa CNTT",
    implementationTime: "12 Tháng",
    status: "Đạt",
  },
];

const ExperienceInitiatives: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [openMenuInitiativeId, setOpenMenuInitiativeId] = useState<
    string | null
  >(null);
  const [menuPlacement, setMenuPlacement] = useState<"bottom" | "top">(
    "bottom"
  );
  const [buttonPosition, setButtonPosition] = useState<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Delete modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInitiativeId, setDeleteInitiativeId] = useState<string | null>(
    null
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuInitiativeId) {
        const currentRef = dropdownRefs.current[openMenuInitiativeId];
        if (currentRef && !currentRef.contains(event.target as Node)) {
          setOpenMenuInitiativeId(null);
        }
      }
    };

    const handleScroll = () => {
      if (openMenuInitiativeId && buttonPosition) {
        // Update position on scroll to keep menu attached to button
        const button = document.querySelector(
          `[data-initiative-id="${openMenuInitiativeId}"]`
        ) as HTMLButtonElement;
        if (button) {
          const rect = button.getBoundingClientRect();
          setButtonPosition({
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
          });
        }
      }
    };

    if (openMenuInitiativeId) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuInitiativeId, buttonPosition]);

  const handleDeleteConfirm = () => {
    if (deleteInitiativeId) {
      console.log("Deleting initiative:", deleteInitiativeId);
      // Here you would implement the actual delete logic
      setShowDeleteConfirm(false);
      setDeleteInitiativeId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setDeleteInitiativeId(null);
  };

  const handleViewDetail = (initiativeId: string) => {
    console.log("Viewing details for initiative:", initiativeId);
    navigate(`/initiatives/detail/${initiativeId}`);
  };

  const handleEdit = (initiativeId: string) => {
    console.log("Editing initiative:", initiativeId);
    navigate(`/initiatives/edit/${initiativeId}`);
  };

  return (
    <PageShell title="Sáng kiến kinh nghiệm">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Sáng kiến kinh nghiệm
          </h1>
          <button
            onClick={() => navigate("/add-new-initiative")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Sáng kiến</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm sáng kiến"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Filters */}
            <div className="flex space-x-4">
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Chọn đơn vị -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Chọn lĩnh vực nghiên cứu -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Chọn năm thực hiện -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white">
                <option>- Trạng thái -</option>
              </select>
            </div>

            {/* Row 3 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8">
                    STT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Tên Sáng kiến
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-40">
                    Lĩnh vực
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Tổng kinh phí
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32">
                    Người đề xuất
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-28">
                    Đơn vị phối hợp
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Thời gian thực hiện
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Trạng thái
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-20">
                    Chức năng
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-8">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight">
                        {item.initiativeName}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-40">
                      {item.field || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      {item.totalCost || ""}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-32">
                      {item.proposer}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-28">
                      {item.coordinatingUnit}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      {item.implementationTime}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.status}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap border-r border-gray-200 w-20">
                      <div
                        className="relative inline-block"
                        ref={(el) => {
                          dropdownRefs.current[item.id] = el;
                        }}
                      >
                        <button
                          data-initiative-id={item.id}
                          className="bg-[#337ab7] text-white px-2 py-1 rounded text-xs font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-1"
                          onClick={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            const rect = target.getBoundingClientRect();
                            const estimatedMenuHeight = 150; // approx height for 3 items with padding
                            const spaceBelow = window.innerHeight - rect.bottom;
                            // More aggressive flipping - flip if less than 150px space below
                            const willOverflowBottom = spaceBelow < 150;
                            setMenuPlacement(
                              willOverflowBottom ? "top" : "bottom"
                            );
                            setButtonPosition({
                              left: rect.left,
                              top: rect.top,
                              right: rect.right,
                              bottom: rect.bottom,
                            });
                            setOpenMenuInitiativeId(
                              openMenuInitiativeId === item.id ? null : item.id
                            );
                          }}
                        >
                          <span>Chọn</span>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        {openMenuInitiativeId === item.id &&
                          createPortal(
                            <div
                              className={
                                `fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[99999] ` +
                                (menuPlacement === "bottom"
                                  ? "mt-2"
                                  : "bottom-full mb-2")
                              }
                              onMouseDown={(e) => {
                                // Prevent the global mousedown outside handler from closing
                                // the menu before the anchor click can navigate
                                e.stopPropagation();
                              }}
                              style={
                                buttonPosition
                                  ? {
                                      left: `${buttonPosition.right - 200}px`, // 200px = w-48 + 16px offset
                                      top:
                                        menuPlacement === "bottom"
                                          ? `${buttonPosition.bottom + 0}px`
                                          : `${buttonPosition.top - 150}px`,
                                      backgroundColor: "white",
                                      zIndex: 99999,
                                    }
                                  : {}
                              }
                            >
                              <div className="py-1 bg-white">
                                <button
                                  onClick={() => {
                                    handleViewDetail(item.id);
                                    setOpenMenuInitiativeId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <Eye className="h-4 w-4 text-blue-600" />
                                  <span>Chi tiết</span>
                                </button>
                                <button
                                  onClick={() => {
                                    handleEdit(item.id);
                                    setOpenMenuInitiativeId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <Pencil className="h-4 w-4 text-amber-600" />
                                  <span>Sửa</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setDeleteInitiativeId(item.id);
                                    setShowDeleteConfirm(true);
                                    setOpenMenuInitiativeId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                  <span>Xóa</span>
                                </button>
                              </div>
                            </div>,
                            document.body
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100000]">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Xác nhận xóa
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn có chắc chắn muốn xóa sáng kiến này không? Hành động này
                  không thể hoàn tác.
                </p>
                <div className="flex space-x-3 justify-center">
                  <button
                    onClick={handleDeleteCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Xóa
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
};

export default ExperienceInitiatives;
